/**
 * Guitar-mode SFX layers built on top of the shared `sfx.ts` one-shots.
 * Lives separately because Guitar's "real amp on a real stage" identity
 * (chord stabs, power chords, ring-outs, slapback echo, sub on every
 * tap) is mode-specific — Piano + Circles want a cleaner, drier feel
 * and keep their existing flat hit-click path.
 *
 * All layers route through whatever `destination` the caller hands in
 * — typically the SFX bus, which in Guitar's case sits behind the
 * `GuitarStageBus` (compressor + soft saturator + small reverb send).
 * Levels are pre-trimmed so the SFX volume slider still scales the
 * combined signal linearly.
 */

import { midiToHz, scheduleHitClick } from './sfx';

/**
 * Combo threshold at which a perfect hit also fires the saturated
 * power chord layer. Same value the session uses for its "big combo"
 * visual cues so the audio + visual peak together.
 */
export const POWER_CHORD_COMBO_THRESHOLD = 25;

/**
 * Options for {@link scheduleGuitarHitLayers}. Extends the shared
 * {@link import('./sfx').HitClickOptions} with Guitar-specific cues
 * the layer-stacker needs: combo count (gates power chord), dual-gem
 * flag (gates chord stab), hold-release flag (gates ring-out).
 */
export interface GuitarHitLayersOptions {
  pan?: number;
  comboNote?: number;
  combo?: number;
  isDual?: boolean;
  isHoldRelease?: boolean;
}

/**
 * Compose the full Guitar hit-feedback stack for one resolution.
 * Returns `true` when the slow-decay ring-out layer was scheduled so
 * the caller can extend its SFX voice hold to cover the tail.
 *
 * Layer order (all into the same `destination` so the stage bus glues
 * them together):
 *   1. Shared `scheduleHitClick` baseline (body + click + sparkle +
 *      sub on perfect).
 *   2. Slapback echo — tiny attenuated tap ~80 ms after the main hit.
 *      Fires for both perfect + good; misses get no echo.
 *   3. Sub-bass punch — only for `good` (perfect already has its own
 *      sub in the baseline click).
 *   4. Chord stab — only when the lane event is the `dual` half of a
 *      chord pair.
 *   5. Power chord — only when verdict is perfect AND combo is hot
 *      ({@link POWER_CHORD_COMBO_THRESHOLD}+).
 *   6. Hold-release ring-out — only on a successful hold release.
 */
export function scheduleGuitarHitLayers(
  ctx: AudioContext,
  destination: AudioNode,
  t0: number,
  verdict: 'perfect' | 'good' | 'miss',
  opts: GuitarHitLayersOptions,
): boolean {
  const pan = opts.pan ?? 0;
  const comboNote = opts.comboNote ?? 0;
  const combo = opts.combo ?? 0;
  const isDual = opts.isDual ?? false;
  const isHoldRelease = opts.isHoldRelease ?? false;

  scheduleHitClick(ctx, destination, t0, verdict, { pan, comboNote });

  if (verdict === 'miss') {
    return false;
  }

  scheduleSlapbackEcho(ctx, destination, t0, verdict, pan);

  if (verdict === 'good') {
    scheduleGuitarSubPunch(ctx, destination, t0, 0.55);
  }

  if (isDual) {
    scheduleChordStab(ctx, destination, t0, comboNote, pan);
  }

  if (verdict === 'perfect' && combo >= POWER_CHORD_COMBO_THRESHOLD) {
    schedulePowerChord(ctx, destination, t0, comboNote, pan);
  }

  if (isHoldRelease) {
    scheduleHoldReleaseRingOut(ctx, destination, t0, comboNote, pan);
    return true;
  }

  return false;
}

/** Combined trim so the stack of new layers stays under the master headroom. */
const GUITAR_LAYER_TRIM = 0.55;

/**
 * Pentatonic root used by power chords and ring-outs. Lower octave than
 * `PENTATONIC_MIDI` in `sfx.ts` so the chord sits *below* the sparkle
 * layer rather than fighting it — chord = body, sparkle = top.
 */
const CHORD_ROOT_MIDI: ReadonlyArray<number> = [48, 50, 52, 55, 57, 60, 62, 64];

const CHORD_ROOT_LENGTH = CHORD_ROOT_MIDI.length;

function pentatonicRootHz(comboNote: number, octaveShift = 0): number {
  const idx = ((comboNote % CHORD_ROOT_LENGTH) + CHORD_ROOT_LENGTH) % CHORD_ROOT_LENGTH;
  return midiToHz(CHORD_ROOT_MIDI[idx] + octaveShift * 12);
}

function panNode(ctx: AudioContext, destination: AudioNode, pan: number): AudioNode {
  if (typeof ctx.createStereoPanner !== 'function') {
    return destination;
  }
  const panner = ctx.createStereoPanner();
  panner.pan.value = Math.max(-1, Math.min(1, pan));
  panner.connect(destination);
  return panner;
}

/**
 * Fatter triadic stab for dual gems. Triggered when the engine fires
 * the `dual` half of a chord-pair resolution so each chord lands with
 * a chunky harmonic punch the regular click can't produce alone.
 * Triangle-wave triad (root + major 3rd + 5th) so it reads as
 * "musical chord" rather than "noise burst".
 */
export function scheduleChordStab(
  ctx: AudioContext,
  destination: AudioNode,
  t0: number,
  comboNote: number,
  pan = 0,
): void {
  const out = panNode(ctx, destination, pan);
  const rootHz = pentatonicRootHz(comboNote, 1);
  const ratios = [1, 1.26, 1.5];
  for (const r of ratios) {
    const osc = ctx.createOscillator();
    osc.type = 'triangle';
    osc.frequency.value = rootHz * r;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0, t0);
    gain.gain.linearRampToValueAtTime(0.11 * GUITAR_LAYER_TRIM, t0 + 0.006);
    gain.gain.exponentialRampToValueAtTime(0.001, t0 + 0.18);
    osc.connect(gain).connect(out);
    osc.start(t0);
    osc.stop(t0 + 0.2);
  }
}

/**
 * Saturated sawtooth power chord (root + 5th + octave) layered onto
 * perfect hits when the combo's already hot. Reads as "the rig is
 * cranked" — pairs with the stage bus's soft saturator so the chord
 * gets a touch of grit without ever clipping the master.
 */
export function schedulePowerChord(
  ctx: AudioContext,
  destination: AudioNode,
  t0: number,
  comboNote: number,
  pan = 0,
): void {
  const out = panNode(ctx, destination, pan);
  const rootHz = pentatonicRootHz(comboNote, 0);
  const ratios = [1, 1.5, 2];
  for (const r of ratios) {
    const osc = ctx.createOscillator();
    osc.type = 'sawtooth';
    osc.frequency.value = rootHz * r;
    const filter = ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.value = 760;
    filter.Q.value = 0.85;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0, t0);
    gain.gain.linearRampToValueAtTime(0.14 * GUITAR_LAYER_TRIM, t0 + 0.005);
    gain.gain.exponentialRampToValueAtTime(0.001, t0 + 0.22);
    osc.connect(filter).connect(gain).connect(out);
    osc.start(t0);
    osc.stop(t0 + 0.24);
  }
}

/**
 * Ringing chord that decays for ~550 ms when a hold completes
 * cleanly. Distinct from the regular hit click so the longer
 * commitment earns its own sonic payoff — players hear the
 * difference between "tapped a note" and "rode out a sustain".
 */
export function scheduleHoldReleaseRingOut(
  ctx: AudioContext,
  destination: AudioNode,
  t0: number,
  comboNote: number,
  pan = 0,
): void {
  const out = panNode(ctx, destination, pan);
  const rootHz = pentatonicRootHz(comboNote, 0);
  const ratios = [1, 1.5, 2];
  for (const r of ratios) {
    const osc = ctx.createOscillator();
    osc.type = 'triangle';
    osc.frequency.value = rootHz * r;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0, t0);
    gain.gain.linearRampToValueAtTime(0.13 * GUITAR_LAYER_TRIM, t0 + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.001, t0 + 0.55);
    osc.connect(gain).connect(out);
    osc.start(t0);
    osc.stop(t0 + 0.6);
  }
}

/**
 * Tiny attenuated echo of the hit ~80 ms after the main click — a
 * cheap "amp in a hall" cue without the cost of a real delay line.
 * Single sine pulse panned with the source so it sits in the same
 * spatial slot as the hit it follows.
 */
export function scheduleSlapbackEcho(
  ctx: AudioContext,
  destination: AudioNode,
  t0: number,
  variant: 'perfect' | 'good',
  pan = 0,
): void {
  const out = panNode(ctx, destination, pan);
  const tap = t0 + 0.085;
  const osc = ctx.createOscillator();
  osc.type = 'sine';
  const startHz = variant === 'perfect' ? 220 : 175;
  const endHz = variant === 'perfect' ? 140 : 110;
  osc.frequency.setValueAtTime(startHz, tap);
  osc.frequency.exponentialRampToValueAtTime(endHz, tap + 0.04);
  const peak = variant === 'perfect' ? 0.10 : 0.07;
  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0, tap);
  gain.gain.linearRampToValueAtTime(peak * GUITAR_LAYER_TRIM, tap + 0.004);
  gain.gain.exponentialRampToValueAtTime(0.001, tap + 0.09);
  osc.connect(gain).connect(out);
  osc.start(tap);
  osc.stop(tap + 0.1);
}

/**
 * Sub-bass punch for goods (and any hit the shared `scheduleHitClick`
 * doesn't already cover). Phone speakers won't render it; headphones
 * and monitors get a continuous low-end through the run. `intensity`
 * in [0, 1] scales the peak so callers can tune per-verdict (good
 * gets a softer punch than perfect).
 */
export function scheduleGuitarSubPunch(
  ctx: AudioContext,
  destination: AudioNode,
  t0: number,
  intensity: number,
): void {
  const osc = ctx.createOscillator();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(72, t0);
  osc.frequency.exponentialRampToValueAtTime(40, t0 + 0.03);
  const gain = ctx.createGain();
  const safeIntensity = Math.max(0, Math.min(1, intensity));
  const peak = 0.32 * safeIntensity;
  gain.gain.setValueAtTime(0, t0);
  gain.gain.linearRampToValueAtTime(peak * GUITAR_LAYER_TRIM, t0 + 0.004);
  gain.gain.exponentialRampToValueAtTime(0.001, t0 + 0.05);
  osc.connect(gain).connect(destination);
  osc.start(t0);
  osc.stop(t0 + 0.07);
}

/**
 * Procedural reverb impulse — short stereo noise burst with
 * exponential decay, suitable as a {@link ConvolverNode.buffer}.
 * Kept ~220 ms so the per-hit convolution cost stays cheap on
 * mid-range mobile devices.
 */
export function buildReverbImpulse(
  ctx: AudioContext,
  durationSec = 0.22,
  decay = 3.5,
): AudioBuffer {
  const length = Math.max(1, Math.floor(ctx.sampleRate * durationSec));
  const buffer = ctx.createBuffer(2, length, ctx.sampleRate);
  for (let ch = 0; ch < 2; ch++) {
    const data = buffer.getChannelData(ch);
    for (let i = 0; i < length; i++) {
      const t = i / length;
      data[i] = (Math.random() * 2 - 1) * Math.pow(1 - t, decay);
    }
  }
  return buffer;
}

/**
 * Multi-miss crash boom. Heavier than {@link scheduleComboBreakBoom}:
 * a deeper sub slide, a saturated mid sawtooth pitch-drop, plus a
 * brief noise stutter cluster that sells "the amp blew a fuse". Used
 * for the multi-miss "crash" path (3+ misses in <700 ms) so the
 * player viscerally feels they're nose-diving the run, not just
 * fumbling a single note.
 */
export function scheduleCrashBoom(
  ctx: AudioContext,
  destination: AudioNode,
  t0: number,
): void {
  const subOsc = ctx.createOscillator();
  subOsc.type = 'sine';
  subOsc.frequency.setValueAtTime(140, t0);
  subOsc.frequency.exponentialRampToValueAtTime(26, t0 + 0.48);
  const subGain = ctx.createGain();
  subGain.gain.setValueAtTime(0.65 * GUITAR_LAYER_TRIM, t0);
  subGain.gain.exponentialRampToValueAtTime(0.001, t0 + 0.52);
  subOsc.connect(subGain).connect(destination);
  subOsc.start(t0);
  subOsc.stop(t0 + 0.55);

  const ripOsc = ctx.createOscillator();
  ripOsc.type = 'sawtooth';
  ripOsc.frequency.setValueAtTime(280, t0);
  ripOsc.frequency.exponentialRampToValueAtTime(55, t0 + 0.4);
  const shaper = ctx.createWaveShaper();
  shaper.curve = createSoftSaturatorCurve(0.65);
  shaper.oversample = '2x';
  const ripFilter = ctx.createBiquadFilter();
  ripFilter.type = 'lowpass';
  ripFilter.frequency.setValueAtTime(1100, t0);
  ripFilter.frequency.exponentialRampToValueAtTime(160, t0 + 0.42);
  ripFilter.Q.value = 1.2;
  const ripGain = ctx.createGain();
  ripGain.gain.setValueAtTime(0, t0);
  ripGain.gain.linearRampToValueAtTime(0.38 * GUITAR_LAYER_TRIM, t0 + 0.006);
  ripGain.gain.exponentialRampToValueAtTime(0.001, t0 + 0.46);
  ripOsc.connect(shaper).connect(ripFilter).connect(ripGain).connect(destination);
  ripOsc.start(t0);
  ripOsc.stop(t0 + 0.5);

  // Three rapid noise transients give the "stutter / amp coughing"
  // texture — bandpass-tuned to sit in the mid-range so they don't
  // mask the sub slide underneath.
  const stutterCount = 3;
  const stutterSpacing = 0.06;
  for (let i = 0; i < stutterCount; i += 1) {
    const start = t0 + 0.02 + i * stutterSpacing;
    const dur = 0.045;
    const samples = Math.max(1, Math.ceil(ctx.sampleRate * dur));
    const buffer = ctx.createBuffer(1, samples, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let j = 0; j < samples; j += 1) {
      data[j] = (Math.random() * 2 - 1) * (1 - j / samples);
    }
    const noise = ctx.createBufferSource();
    noise.buffer = buffer;
    const filter = ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.value = 420 - i * 60;
    filter.Q.value = 1.6;
    const gain = ctx.createGain();
    const peak = (0.34 - i * 0.05) * GUITAR_LAYER_TRIM;
    gain.gain.setValueAtTime(peak, start);
    gain.gain.exponentialRampToValueAtTime(0.001, start + dur);
    noise.connect(filter).connect(gain).connect(destination);
    noise.start(start);
  }
}

/**
 * Soft-clip curve for a `WaveShaperNode`. `amount` in [0, 1] sweeps
 * from transparent (linear) at 0 to a heavy tanh soft-clip at 1. The
 * Guitar stage bus uses a low amount (~0.22) so the layered hits get
 * glued together without ever sounding distorted.
 */
export function createSoftSaturatorCurve(amount = 0.22): Float32Array<ArrayBuffer> {
  const samples = 256;
  // Explicit ArrayBuffer backing so the result types as
  // `Float32Array<ArrayBuffer>` for `WaveShaperNode.curve` (which
  // refuses `Float32Array<ArrayBufferLike>` since TS 5.7's stricter
  // typed-array generics).
  const curve = new Float32Array(new ArrayBuffer(samples * Float32Array.BYTES_PER_ELEMENT));
  const k = Math.max(0, Math.min(1, amount)) * 6;
  if (k === 0) {
    for (let i = 0; i < samples; i++) {
      curve[i] = (i / (samples - 1)) * 2 - 1;
    }
    return curve;
  }
  const norm = Math.tanh(k);
  for (let i = 0; i < samples; i++) {
    const x = (i / (samples - 1)) * 2 - 1;
    curve[i] = Math.tanh(k * x) / norm;
  }
  return curve;
}
